import { ColumnDef } from '@tanstack/react-table';
import * as React from 'react';
import {
  ChatBubbleLeftIcon,
  DocumentMagnifyingGlassIcon,
  TagIcon,
} from '@heroicons/react/24/outline';
import { TFunction } from 'i18next';

export interface CustomAttribute {
  id: string;
  name: string;
  value: string;
  type: string;
  updated_at?: string;
}

export interface Customer {
  _id: string;
  person_id: string;
  name: string;
  email?: string;
  phone?: string;
  created_at: string;
  last_activity?: string;
  custom_attributes?: CustomAttribute[];
}

export const createCustomerColumns = (
  navigate: (path: string) => void,
  t: TFunction,
  onCustomerAction?: (customerId: string, action: string) => void,
): ColumnDef<Customer>[] => [
  {
    accessorKey: 'name',
    header: t('table.customer_name', 'Customer Name'),
    enableSorting: true,
    cell: (info) => {
      const name = info.getValue() as string;
      const row = info.row.original;
      
      // Generate initials from name
      const getInitials = (fullName: string) => {
        return fullName
          .trim()
          .split(' ')
          .map(word => word.charAt(0).toUpperCase())
          .slice(0, 2) // Take only first 2 initials
          .join('');
      };

      const initials = getInitials(name);
      
      return React.createElement(
        'div',
        {
          className: 'flex items-center gap-3',
        },
        [
          // Avatar with initials
          React.createElement(
            'div',
            {
              className: 'avatar placeholder',
              key: 'avatar',
            },
            React.createElement(
              'div',
              {
                className: 'bg-primary text-primary-content rounded-full w-10 h-10',
                style: {
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                },
              },
              React.createElement(
                'span',
                {},
                initials,
              ),
            ),
          ),
          // Name and contact info
          React.createElement(
            'div',
            {
              key: 'info',
            },
            [
              React.createElement(
                'button',
                {
                  className: 'font-medium text-base-content hover:text-primary cursor-pointer text-left',
                  onClick: () => {
                    const current = window.location.pathname.replace(/\/$/, '');
                    navigate(`${current}/${row.person_id}`);
                  },
                  key: 'name',
                },
                name,
              ),
              row.email && React.createElement(
                'div',
                {
                  className: 'text-sm text-base-content/70',
                  key: 'email',
                },
                row.email,
              ),
              row.phone && React.createElement(
                'div',
                {
                  className: 'text-sm text-base-content/70',
                  key: 'phone',
                },
                row.phone,
              ),
            ].filter(Boolean),
          ),
        ],
      );
    },
  },
  {
    accessorKey: 'created_at',
    header: t('table.created_at', 'Created'),
    enableSorting: true,
    cell: (info) => {
      const value = info.getValue() as string;
      if (!value) return '-';

      const date = new Date(value);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      const time = date.toLocaleTimeString('es-CO', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });

      return React.createElement(
        'div',
        {
          className: 'text-sm',
        },
        [
          React.createElement(
            'div',
            {
              className: 'font-medium',
              key: 'date',
            },
            `${day}/${month}/${year}`,
          ),
          React.createElement(
            'div',
            {
              className: 'text-base-content/70',
              key: 'time',
            },
            time,
          ),
        ],
      );
    },
  },
  {
    accessorKey: 'last_activity',
    header: t('table.last_activity', 'Last Activity'),
    enableSorting: true,
    cell: (info) => {
      const value = info.getValue() as string;
      if (!value) {
        return React.createElement(
          'span',
          {
            className: 'text-base-content/50 text-sm',
          },
          t('table.no_activity', 'No activity'),
        );
      }

      const date = new Date(value);
      const now = new Date();
      const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
      const diffInDays = Math.floor(diffInHours / 24);

      let timeAgo = '';
      if (diffInDays > 0) {
        timeAgo = t('table.days_ago', '{{count}} days ago', { count: diffInDays });
      } else if (diffInHours > 0) {
        timeAgo = t('table.hours_ago', '{{count}} hours ago', { count: diffInHours });
      } else {
        timeAgo = t('table.recently', 'Recently');
      }

      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();

      return React.createElement(
        'div',
        {
          className: 'text-sm',
        },
        [
          React.createElement(
            'div',
            {
              className: 'font-medium',
              key: 'date',
            },
            `${day}/${month}/${year}`,
          ),
          React.createElement(
            'div',
            {
              className: 'text-base-content/70',
              key: 'relative',
            },
            timeAgo,
          ),
        ],
      );
    },
  },
  {
    accessorKey: 'custom_attributes',
    header: t('table.custom_attributes', 'Atributos'),
    enableSorting: false,
    cell: (info) => {
      const attributes = info.getValue() as CustomAttribute[] || [];
      const count = attributes.length;
      
      if (count === 0) {
        return React.createElement(
          'span',
          {
            className: 'text-base-content/50 text-sm',
          },
          t('table.no_attributes', 'Sin atributos'),
        );
      }

      return React.createElement(
        'div',
        {
          className: 'flex items-center gap-2',
        },
        [
          React.createElement(TagIcon, {
            className: 'h-4 w-4 text-primary',
            key: 'icon',
          }),
          React.createElement(
            'span',
            {
              className: 'badge badge-primary badge-sm',
              key: 'count',
            },
            count.toString(),
          ),
        ],
      );
    },
  },
  {
    id: 'actions',
    header: t('table.actions', 'Actions'),
    enableSorting: false,
    cell: ({ row }) => {
      const customer = row.original;
      
      return React.createElement(
        'div',
        {
          className: 'flex items-center gap-2',
        },
        [
          // View details button
          React.createElement(
            'button',
            {
              className: 'btn btn-ghost btn-sm',
              title: t('table.view_details', 'View Details'),
              onClick: () => {
                const current = window.location.pathname.replace(/\/$/, '');
                navigate(`${current}/${customer.person_id}`);
              },
              key: 'details',
            },
            React.createElement(DocumentMagnifyingGlassIcon, {
              className: 'h-4 w-4',
            }),
          ),
          // Chat button
          React.createElement(
            'button',
            {
              className: 'btn btn-ghost btn-sm',
              title: t('table.start_chat', 'Start Chat'),
              onClick: () => {
                if (onCustomerAction) {
                  onCustomerAction(customer.person_id, 'chat');
                }
              },
              key: 'chat',
            },
            React.createElement(ChatBubbleLeftIcon, {
              className: 'h-4 w-4',
            }),
          ),
        ],
      );
    },
  },
];
